
import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type CookieType = "essential" | "functional" | "analytics" | "thirdParty";

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  thirdParty: boolean;
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  hasConsented: boolean;
  showBanner: boolean;
  acceptAll: () => void;
  acceptEssential: () => void;
  savePreferences: (prefs: CookiePreferences) => void;
  openSettings: () => void;
  closeBanner: () => void;
  isAllowed: (cookieType: CookieType) => boolean;
}

const defaultPreferences: CookiePreferences = {
  essential: true, // Essential cookies are always allowed
  functional: false,
  analytics: false,
  thirdParty: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState<boolean>(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem("cookiePreferences");
    const consentGiven = localStorage.getItem("cookieConsent");
    
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (err) {
        console.error("Error parsing cookie preferences:", err);
        setPreferences(defaultPreferences);
      }
    }
    
    if (consentGiven === "true") {
      setHasConsented(true);
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const saveToStorage = (prefs: CookiePreferences, consented: boolean = true) => {
    localStorage.setItem("cookiePreferences", JSON.stringify(prefs));
    localStorage.setItem("cookieConsent", consented.toString());
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      thirdParty: true,
    };
    setPreferences(allAccepted);
    setHasConsented(true);
    setShowBanner(false);
    saveToStorage(allAccepted);
  };

  const acceptEssential = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      thirdParty: false,
    };
    setPreferences(essentialOnly);
    setHasConsented(true);
    setShowBanner(false);
    saveToStorage(essentialOnly);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    const updatedPrefs = { ...prefs, essential: true }; // Essential always true
    setPreferences(updatedPrefs);
    setHasConsented(true);
    setShowBanner(false);
    saveToStorage(updatedPrefs);
  };

  const openSettings = () => {
    setShowBanner(true);
  };

  const closeBanner = () => {
    setShowBanner(false);
  };

  const isAllowed = (cookieType: CookieType): boolean => {
    if (!hasConsented) return cookieType === "essential";
    return preferences[cookieType];
  };

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        hasConsented,
        showBanner,
        acceptAll,
        acceptEssential,
        savePreferences,
        openSettings,
        closeBanner,
        isAllowed,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};
